import { FormControl, FormHelperText, Stack } from "@mui/joy";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Accordion from "@mui/joy/Accordion";
import AccordionDetails from "@mui/joy/AccordionDetails";
import AccordionGroup from "@mui/joy/AccordionGroup";
import AccordionSummary from "@mui/joy/AccordionSummary";
import Input from "@mui/joy/Input";
import Textarea from "@mui/joy/Textarea";
import Button from "@mui/joy/Button";
import { api } from "../../../../interceptors/api";
import { useLayout } from "../../../LayoutContext";
import { DeviceDto } from "../../dto/DeviceDto";
import { getExhibitionConfig } from "../../config/exhibitionConfig";
import "react-toastify/dist/ReactToastify.css";
import "./styles/UserStories.css";

interface StorySharingProps {
  exhibitionId: number;
}

const StorySharing = ({ exhibitionId }: StorySharingProps) => {
  const [devices, setDevices] = useState<DeviceDto[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [newDeviceName, setNewDeviceName] = useState<string>("");

  const [storyText, setStoryText] = useState<string>("");

  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const [isTextSubmitEnabled, setIsTextSubmitEnabled] = useState(false);
  const [isAudioSubmitEnabled, setIsAudioSubmitEnabled] = useState(false);

  const [isSubmittingText, setIsSubmittingText] = useState(false);
  const [isSubmittingAudio, setIsSubmittingAudio] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setExhibitionMode } = useLayout();

  const exhibitionConfig = getExhibitionConfig(exhibitionId);

  useEffect(() => {
    setExhibitionMode(true);
    return () => {
      setExhibitionMode(false);
    };
  }, [setExhibitionMode]);

  useEffect(() => {
    api
      .get<DeviceDto[]>("/api/device/all")
      .then((res) => {
        setDevices(res.data);

        // If exhibition has a fixed device, auto-select it
        if (exhibitionConfig?.fixedDeviceId) {
          setSelectedDevice(exhibitionConfig.fixedDeviceId.toString());
        } else {
          // Select device if passed by URL param
          const devIdFromUrl = searchParams.get("deviceId");
          if (devIdFromUrl) {
            const found = res.data.find(
              (d) => d.id.toString() === devIdFromUrl,
            );
            if (found) {
              setSelectedDevice(devIdFromUrl);
            }
          }
        }
      })
      .catch((err) => {
        console.error("Error loading devices:", err);
        toast.error("Failed to load device list.");
      });
  }, [searchParams, exhibitionConfig]);

  useEffect(() => {
    const hasDevice =
      selectedDevice !== "" ||
      (!!exhibitionConfig?.allowAddingNewDevices &&
        newDeviceName.trim() !== "");
    setIsTextSubmitEnabled(hasDevice && storyText.trim().length > 0);
  }, [selectedDevice, newDeviceName, storyText, exhibitionConfig]);

  useEffect(() => {
    const hasDevice =
      selectedDevice !== "" ||
      (!!exhibitionConfig?.allowAddingNewDevices &&
        newDeviceName.trim() !== "");
    setIsAudioSubmitEnabled(hasDevice && audioBlob !== null);
  }, [selectedDevice, newDeviceName, audioBlob, exhibitionConfig]);

  const handleDeviceSelect = (
    event: React.SyntheticEvent | null,
    newValue: string | null,
  ) => {
    setSelectedDevice(newValue as string);
    setNewDeviceName("");
  };

  const handleNewDeviceChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setNewDeviceName(ev.target.value);
    setSelectedDevice("");
  };

  const handleTextChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    setStoryText(ev.target.value);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const sizeInMB = blob.size / (1024 * 1024);
        if (sizeInMB > 4.5) {
          toast.error("Audio exceeds 5 MB. Please record a shorter clip.");
          return;
        }
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      toast.error("Cannot access microphone.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      audioChunksRef.current = [];
    }
  };

  const submitText = async () => {
    if (!isTextSubmitEnabled || isSubmittingText) return;

    setIsSubmittingText(true);

    const payload: any = {
      storyText: storyText.trim(),
      exhibitionId: exhibitionId,
    };

    let endpoint = "";
    if (selectedDevice) {
      endpoint = `/api/user-story/submit-text/device/${selectedDevice}/exhibition/${exhibitionId}`;
    } else {
      endpoint = `/api/user-story/submit-text/new-device/exhibition/${exhibitionId}`;
      payload.deviceName = newDeviceName.trim();
    }

    try {
      const res = await api.post(endpoint, payload);
      const data = res.data;
      if (data.statusCode === 200) {
        toast.success(`Thanks for sharing your story!`);
        setStoryText("");
      } else {
        toast.error(data.message || "Server error while submitting text.");
      }
    } catch (err: any) {
      console.error("Error submitting text:", err);
      toast.error(`Error submitting text: ${err.message || err}`);
    } finally {
      setIsSubmittingText(false);
    }
  };

  const submitAudio = async () => {
    if (!isAudioSubmitEnabled || !audioBlob || isSubmittingAudio) return;

    setIsSubmittingAudio(true);

    const formData = new FormData();
    formData.append("audio", audioBlob, "story.webm");
    formData.append("exhibitionId", exhibitionId.toString());

    let endpoint = "";
    if (selectedDevice) {
      endpoint = `/api/user-story/submit-audio/device/${selectedDevice}/exhibition/${exhibitionId}`;
    } else {
      endpoint = `/api/user-story/submit-audio/new-device/exhibition/${exhibitionId}`;
      formData.append("deviceName", newDeviceName.trim());
    }

    try {
      const res = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const data = res.data;
      if (data.statusCode === 200) {
        toast.success(`Thanks for sharing your story!`);
        setAudioBlob(null);
        setAudioUrl("");
      } else {
        toast.error(data.message || "Server error while uploading audio.");
      }
    } catch (err: any) {
      console.error("Error uploading audio:", err);
      toast.error(`Error uploading audio: ${err.message || err}`);
    } finally {
      setIsSubmittingAudio(false);
    }
  };

  return (
    <div className="story-sharing-container">
      <ToastContainer position="bottom-center" autoClose={3000} />
      <h1>{exhibitionConfig?.customTitle || "Share Your Device Story"}</h1>

      {exhibitionConfig?.allowDeviceSelection && (
        <div className="device-selection">
          <FormHelperText>Pick an existing device</FormHelperText>
          <FormControl>
            <Select
              value={selectedDevice}
              onChange={handleDeviceSelect}
              id="device-select"
            >
              <Option value="">— Select Device —</Option>
              {devices.map((d) => (
                <Option key={d.id} value={d.id.toString()}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </FormControl>

          {exhibitionConfig?.allowAddingNewDevices && (
            <>
              <div className="or-divider">— Or add a new device —</div>

              <FormHelperText>New device name</FormHelperText>
              <FormControl>
                <Input
                  id="new-device-input"
                  placeholder="Device name"
                  variant="outlined"
                  type="text"
                  value={newDeviceName}
                  onChange={handleNewDeviceChange}
                />
              </FormControl>
            </>
          )}
        </div>
      )}

      <AccordionGroup size="md" className="story-accordions">
        <Accordion defaultExpanded>
          <AccordionSummary>
            <h2>Write Your Story (Text)</h2>
          </AccordionSummary>
          <AccordionDetails>
            <div className="text-pane">
              <Textarea
                minRows={5}
                placeholder="Type your story here…"
                value={storyText}
                onChange={handleTextChange}
              />

              <Button
                sx={{ mt: 2 }}
                variant="solid"
                color="primary"
                onClick={submitText}
                disabled={!isTextSubmitEnabled || isSubmittingText}
                loading={isSubmittingText}
              >
                Share Story Text
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary>
            <h2>Record Your Story (Audio)</h2>
          </AccordionSummary>
          <AccordionDetails>
            <div className="audio-pane">
              <div className="record-controls">
                {isRecording ? (
                  <Button
                    variant="outlined"
                    color="danger"
                    onClick={stopRecording}
                  >
                    Stop Recording
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={startRecording}
                  >
                    Start Recording
                  </Button>
                )}
              </div>

              {audioUrl && (
                <div className="audio-preview">
                  <p>Preview your recording:</p>
                  <audio controls src={audioUrl}></audio>
                </div>
              )}

              <Button
                sx={{ mt: 2 }}
                variant="solid"
                color="primary"
                onClick={submitAudio}
                disabled={!isAudioSubmitEnabled || isSubmittingAudio}
                loading={isSubmittingAudio}
              >
                Share Story Audio
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
      </AccordionGroup>
      <Stack justifyContent="center" sx={{ mt: 4 }}>
        <Button
          variant="solid"
          color="neutral"
          onClick={() => {
            // Navigate to the appropriate explore-stories route based on exhibition
            if (exhibitionId === 4) {
              navigate("/exhibits/imac/explore-stories");
            } else {
              navigate("/exhibits/werkschau/explore-stories");
            }
          }}
        >
          Explore User Stories
        </Button>
      </Stack>
    </div>
  );
};

export default StorySharing;
